import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import {
  ChevronLeft,
  ChevronRight,
  Flame,
  Sparkles,
  X,
  Check,
  SkipForward,
} from 'lucide-react';
import { useApp } from '@/store';
import { useT, type I18nKey } from '@/lib/i18n';
import { Button } from '@/components/ui/button';

type Step = {
  route?: string | (() => string | undefined);
  selector?: string;
  titleKey: I18nKey;
  bodyKey: I18nKey;
  icon?: 'flame' | 'sparkles';
};

function firstCustomerId() {
  return useApp.getState().customers[0]?.id;
}
function firstExtinguisherRoute() {
  const c = useApp.getState().customers[0];
  const ext = c?.extinguishers[0];
  if (!c || !ext) return undefined;
  return `/customer/${c.id}/extinguisher/${ext.id}`;
}

const steps: Step[] = [
  {
    route: '/overview',
    titleKey: 'tour.welcome.title',
    bodyKey: 'tour.welcome.body',
    icon: 'flame',
  },
  {
    selector: '[data-tour="sidebar"]',
    titleKey: 'tour.nav.title',
    bodyKey: 'tour.nav.body',
  },
  {
    route: '/overview',
    selector: '[data-tour="overview-table"]',
    titleKey: 'tour.overview.title',
    bodyKey: 'tour.overview.body',
  },
  {
    route: '/map',
    selector: '[data-tour="map-view"]',
    titleKey: 'tour.map.title',
    bodyKey: 'tour.map.body',
  },
  {
    route: '/reports',
    titleKey: 'tour.reports.title',
    bodyKey: 'tour.reports.body',
  },
  {
    route: () => {
      const id = firstCustomerId();
      return id ? `/customer/${id}` : undefined;
    },
    selector: '[data-tour="customer-detail"]',
    titleKey: 'tour.customer.title',
    bodyKey: 'tour.customer.body',
  },
  {
    route: () => {
      const id = firstCustomerId();
      return id ? `/customer/${id}/extinguishers` : undefined;
    },
    selector: '[data-tour="add-extinguisher"]',
    titleKey: 'tour.addExt.title',
    bodyKey: 'tour.addExt.body',
  },
  {
    route: firstExtinguisherRoute,
    selector: '[data-tour="extinguisher-edit"]',
    titleKey: 'tour.editExt.title',
    bodyKey: 'tour.editExt.body',
  },
  {
    route: () => {
      const id = firstCustomerId();
      return id ? `/customer/${id}/invoice` : undefined;
    },
    titleKey: 'tour.invoice.title',
    bodyKey: 'tour.invoice.body',
  },
  {
    selector: '[data-tour="online-btn"]',
    titleKey: 'tour.offline.title',
    bodyKey: 'tour.offline.body',
  },
  {
    selector: '[data-tour="lang-btn"]',
    titleKey: 'tour.lang.title',
    bodyKey: 'tour.lang.body',
  },
  {
    selector: '[data-tour="help-btn"]',
    titleKey: 'tour.ready.title',
    bodyKey: 'tour.ready.body',
    icon: 'sparkles',
  },
];

const CARD_W_DEFAULT = 340;
const CARD_H = 220;
const MARGIN = 16;
const PAD = 8;

function getCardW() {
  return Math.min(CARD_W_DEFAULT, window.innerWidth - MARGIN * 2);
}

function scrollIntoViewIfNeeded(el: HTMLElement) {
  const r = el.getBoundingClientRect();
  if (
    r.top < 0 ||
    r.bottom > window.innerHeight ||
    r.left < 0 ||
    r.right > window.innerWidth
  ) {
    el.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center',
    });
  }
}

type Placement = {
  top: number;
  left: number;
  origin: string;
};

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function placeCard(rect: DOMRect | null): Placement {
  const CARD_W = getCardW();
  if (!rect) {
    return {
      top: window.innerHeight / 2 - CARD_H / 2,
      left: window.innerWidth / 2 - CARD_W / 2,
      origin: 'center',
    };
  }
  const rightSpace = window.innerWidth - (rect.right + PAD);
  const leftSpace = rect.left - PAD;
  const bottomSpace = window.innerHeight - (rect.bottom + PAD);
  const topSpace = rect.top - PAD;

  if (rightSpace >= CARD_W + MARGIN) {
    return {
      top: clamp(
        rect.top + rect.height / 2 - CARD_H / 2,
        MARGIN,
        window.innerHeight - CARD_H - MARGIN
      ),
      left: rect.right + PAD + 12,
      origin: 'left center',
    };
  }
  if (leftSpace >= CARD_W + MARGIN) {
    return {
      top: clamp(
        rect.top + rect.height / 2 - CARD_H / 2,
        MARGIN,
        window.innerHeight - CARD_H - MARGIN
      ),
      left: rect.left - PAD - CARD_W - 12,
      origin: 'right center',
    };
  }
  if (bottomSpace >= CARD_H + MARGIN) {
    return {
      top: rect.bottom + PAD + 12,
      left: clamp(
        rect.left + rect.width / 2 - CARD_W / 2,
        MARGIN,
        window.innerWidth - CARD_W - MARGIN
      ),
      origin: 'top center',
    };
  }
  if (topSpace >= CARD_H + MARGIN) {
    return {
      top: rect.top - PAD - CARD_H - 12,
      left: clamp(
        rect.left + rect.width / 2 - CARD_W / 2,
        MARGIN,
        window.innerWidth - CARD_W - MARGIN
      ),
      origin: 'bottom center',
    };
  }
  return {
    top: window.innerHeight / 2 - CARD_H / 2,
    left: window.innerWidth / 2 - CARD_W / 2,
    origin: 'center',
  };
}

export function Tour() {
  const active = useApp(s => s.tourActive);
  const step = useApp(s => s.tourStep);
  const next = useApp(s => s.nextTourStep);
  const prev = useApp(s => s.prevTourStep);
  const end = useApp(s => s.endTour);
  const t = useT();
  const loc = useLocation();
  const navigate = useNavigate();
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [placement, setPlacement] = useState<Placement | null>(null);
  const rafRef = useRef<number | null>(null);

  const cur = active ? steps[step] : null;

  useEffect(() => {
    if (!active || !cur) return;
    const target = typeof cur.route === 'function' ? cur.route() : cur.route;
    if (target && loc.pathname !== target) {
      navigate(target);
    }
  }, [active, step]);

  useLayoutEffect(() => {
    if (!active || !cur) return;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 40; // ~1.3s at 30fps budget
    const tick = () => {
      if (cancelled) return;
      if (!cur.selector) {
        setRect(null);
        setPlacement(placeCard(null));
        return;
      }
      const el = document.querySelector(cur.selector) as HTMLElement | null;
      if (el) {
        const r = el.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) {
          scrollIntoViewIfNeeded(el);
          rafRef.current = requestAnimationFrame(() => {
            const r2 = el.getBoundingClientRect();
            setRect(r2);
            setPlacement(placeCard(r2));
          });
          return;
        }
      }
      attempts++;
      if (attempts >= maxAttempts) {
        setRect(null);
        setPlacement(placeCard(null));
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    const startId = setTimeout(tick, 60);
    const onResize = () => {
      attempts = 0;
      tick();
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      cancelled = true;
      clearTimeout(startId);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [active, step, loc.pathname]);

  if (!active || !cur || !placement) return null;

  const title = t(cur.titleKey);
  const body = t(cur.bodyKey);
  const isLast = step === steps.length - 1;
  const progress = ((step + 1) / steps.length) * 100;
  const IconComp = cur.icon === 'sparkles' ? Sparkles : Flame;

  const spot = rect
    ? {
        top: Math.max(0, rect.top - PAD),
        left: Math.max(0, rect.left - PAD),
        width: rect.width + PAD * 2,
        height: rect.height + PAD * 2,
      }
    : null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        key="tour-root"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-[2000]"
        aria-live="polite"
      >
        <svg
          className="absolute inset-0 h-full w-full pointer-events-none"
          aria-hidden="true"
        >
          <defs>
            <mask id="tour-mask">
              <rect width="100%" height="100%" fill="white" />
              {spot && (
                <motion.rect
                  initial={false}
                  animate={{
                    x: spot.left,
                    y: spot.top,
                    width: spot.width,
                    height: spot.height,
                  }}
                  transition={{ type: 'spring', stiffness: 260, damping: 30 }}
                  rx={12}
                  ry={12}
                  fill="black"
                />
              )}
            </mask>
            <linearGradient id="tour-stroke" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#fb923c" />
              <stop offset="50%" stopColor="#f97316" />
              <stop offset="100%" stopColor="#ea580c" />
            </linearGradient>
          </defs>
          <rect
            width="100%"
            height="100%"
            fill="rgba(15, 23, 42, 0.72)"
            mask="url(#tour-mask)"
          />
          {spot && (
            <motion.rect
              initial={false}
              animate={{
                x: spot.left,
                y: spot.top,
                width: spot.width,
                height: spot.height,
              }}
              transition={{ type: 'spring', stiffness: 260, damping: 30 }}
              rx={12}
              ry={12}
              fill="none"
              stroke="url(#tour-stroke)"
              strokeWidth={2.5}
            />
          )}
        </svg>

        <div
          className="absolute inset-0"
          onClick={end}
          role="button"
          tabIndex={-1}
          aria-label="close tour"
        />

        <AnimatePresence mode="wait">
          <motion.div
            key={`tip-${step}`}
            initial={{ opacity: 0, scale: 0.92, y: 6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ type: 'spring', stiffness: 260, damping: 24 }}
            className="absolute w-[340px] max-w-[calc(100vw-32px)] overflow-hidden rounded-2xl border bg-card shadow-2xl"
            style={{
              top: placement.top,
              left: placement.left,
              transformOrigin: placement.origin,
            }}
            onClick={e => e.stopPropagation()}
          >
            <div className="h-1 w-full bg-muted">
              <motion.div
                className="h-full flame-gradient"
                initial={false}
                animate={{ width: `${progress}%` }}
                transition={{ type: 'spring', stiffness: 180, damping: 24 }}
              />
            </div>

            <div className="relative flame-gradient-soft px-5 pt-4 pb-3 border-b border-orange-100 dark:border-orange-900/40">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg flame-gradient text-white shadow-md">
                    <IconComp className="h-4 w-4" strokeWidth={2.5} />
                  </div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-orange-700 dark:text-orange-400">
                      {t('tour.step')} {step + 1} / {steps.length}
                    </div>
                    <div className="text-[15px] font-bold leading-tight text-foreground">
                      {title}
                    </div>
                  </div>
                </div>
                <button
                  onClick={end}
                  className="rounded-md p-1 text-muted-foreground hover:bg-white/60 hover:text-foreground transition"
                  aria-label="close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="p-5">
              <p className="text-sm leading-relaxed text-muted-foreground">
                {body}
              </p>
            </div>

            <div className="flex items-center justify-between border-t bg-muted/30 px-4 py-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={end}
                className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-foreground"
              >
                <SkipForward className="h-3 w-3" />
                {t('tour.skip')}
              </Button>
              <div className="flex items-center gap-1.5">
                <div className="mr-2 flex gap-1">
                  {steps.map((_, i) => (
                    <div
                      key={i}
                      className={`h-1.5 rounded-full transition-all ${
                        i === step
                          ? 'w-4 flame-gradient'
                          : i < step
                            ? 'w-1.5 bg-primary/60'
                            : 'w-1.5 bg-muted-foreground/20'
                      }`}
                    />
                  ))}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={prev}
                  disabled={step === 0}
                  className="h-8 w-8 p-0"
                  aria-label="previous"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  onClick={isLast ? end : next}
                  className="h-8 gap-1 flame-gradient text-white shadow-md hover:opacity-90"
                >
                  {isLast ? (
                    <>
                      <Check className="h-3.5 w-3.5" />
                      {t('tour.finish')}
                    </>
                  ) : (
                    <>
                      {t('tour.next')}
                      <ChevronRight className="h-3.5 w-3.5" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}
